import React, { useState, useEffect } from 'react';
import { exercisesAPI } from '../../api';
import { Plus, Search, Filter, Dumbbell, Edit, Trash2, X, ChevronDown } from 'lucide-react';

const MUSCLE_GROUPS = [
  { value: 'chest', label: 'Chest' },
  { value: 'back', label: 'Back' },
  { value: 'shoulders', label: 'Shoulders' },
  { value: 'biceps', label: 'Biceps' },
  { value: 'triceps', label: 'Triceps' },
  { value: 'forearms', label: 'Forearms' },
  { value: 'core', label: 'Core' },
  { value: 'quadriceps', label: 'Quadriceps' },
  { value: 'hamstrings', label: 'Hamstrings' },
  { value: 'glutes', label: 'Glutes' },
  { value: 'calves', label: 'Calves' },
  { value: 'full_body', label: 'Full Body' },
  { value: 'cardio', label: 'Cardio' },
];

const CATEGORIES = [
  { value: 'strength', label: 'Strength' },
  { value: 'cardio', label: 'Cardio' },
  { value: 'flexibility', label: 'Flexibility' },
  { value: 'balance', label: 'Balance' },
  { value: 'plyometric', label: 'Plyometric' },
  { value: 'bodyweight', label: 'Bodyweight' },
];

export function ExerciseLibrary({ userRole }) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterMuscle, setFilterMuscle] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);

  const canEdit = userRole === 'admin' || userRole === 'trainer';

  useEffect(() => {
    fetchExercises();
  }, [filterCategory, filterMuscle, searchQuery]);

  const fetchExercises = async () => {
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (filterMuscle) params.muscle_group = filterMuscle;
      if (searchQuery) params.search = searchQuery;
      
      const response = await exercisesAPI.list(params);
      setExercises(response.data);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (exerciseId) => {
    if (!window.confirm('Are you sure you want to delete this exercise?')) return;
    try {
      await exercisesAPI.delete(exerciseId);
      fetchExercises();
    } catch (error) {
      console.error('Error deleting exercise:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="exercise-library">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exercise Library</h1>
          <p className="text-gray-600">Browse and manage exercises</p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditingExercise(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            data-testid="add-exercise-btn"
          >
            <Plus className="w-5 h-5" />
            Add Exercise
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search exercises..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              data-testid="exercise-search"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
            data-testid="category-filter"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <select
            value={filterMuscle}
            onChange={(e) => setFilterMuscle(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
            data-testid="muscle-filter"
          >
            <option value="">All Muscle Groups</option>
            {MUSCLE_GROUPS.map(mg => (
              <option key={mg.value} value={mg.value}>{mg.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Exercise Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : exercises.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Dumbbell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No exercises found</h3>
          <p className="text-gray-500 mt-1">Try adjusting your filters or add a new exercise</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exercises-grid">
          {exercises.map(exercise => (
            <ExerciseCard
              key={exercise.id}
              exercise={exercise}
              canEdit={canEdit}
              onEdit={() => { setEditingExercise(exercise); setShowForm(true); }}
              onDelete={() => handleDelete(exercise.id)}
            />
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <ExerciseForm
          exercise={editingExercise}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); fetchExercises(); }}
        />
      )}
    </div>
  );
}

function ExerciseCard({ exercise, canEdit, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition" data-testid={`exercise-card-${exercise.id}`}>
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{exercise.name}</h3>
            <div className="flex flex-wrap gap-1 mt-2">
              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                {exercise.category}
              </span>
              {exercise.muscle_groups.slice(0, 2).map(mg => (
                <span key={mg} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  {mg}
                </span>
              ))}
              {exercise.muscle_groups.length > 2 && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  +{exercise.muscle_groups.length - 2}
                </span>
              )}
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-1">
              <button onClick={onEdit} className="p-1 text-gray-400 hover:text-indigo-600">
                <Edit className="w-4 h-4" />
              </button>
              <button onClick={onDelete} className="p-1 text-gray-400 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {exercise.description && (
          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{exercise.description}</p>
        )}

        {exercise.instructions?.length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-sm text-indigo-600 mt-3 hover:text-indigo-700"
          >
            <ChevronDown className={`w-4 h-4 transition ${expanded ? 'rotate-180' : ''}`} />
            {expanded ? 'Hide' : 'Show'} Instructions
          </button>
        )}

        {expanded && exercise.instructions?.length > 0 && (
          <ol className="mt-3 space-y-1 text-sm text-gray-600 list-decimal list-inside">
            {exercise.instructions.map((step, idx) => (
              <li key={idx}>{step}</li>
            ))}
          </ol>
        )}

        {exercise.equipment?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Equipment: {exercise.equipment.join(', ')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ExerciseForm({ exercise, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: exercise?.name || '',
    description: exercise?.description || '',
    instructions: exercise?.instructions || [],
    muscle_groups: exercise?.muscle_groups || [],
    category: exercise?.category || 'strength',
    equipment: exercise?.equipment || [],
    video_url: exercise?.video_url || '',
    image_url: exercise?.image_url || '',
  });
  const [newInstruction, setNewInstruction] = useState('');
  const [newEquipment, setNewEquipment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Exercise name is required');
      return;
    }
    if (formData.muscle_groups.length === 0) {
      setError('Select at least one muscle group');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (exercise) {
        await exercisesAPI.update(exercise.id, formData);
      } else {
        await exercisesAPI.create(formData);
      }
      onSave();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save exercise');
    } finally {
      setSaving(false);
    }
  };

  const toggleMuscleGroup = (mg) => {
    setFormData(prev => ({
      ...prev,
      muscle_groups: prev.muscle_groups.includes(mg)
        ? prev.muscle_groups.filter(m => m !== mg)
        : [...prev.muscle_groups, mg]
    }));
  };

  const addInstruction = () => {
    if (newInstruction.trim()) {
      setFormData(prev => ({
        ...prev,
        instructions: [...prev.instructions, newInstruction.trim()]
      }));
      setNewInstruction('');
    }
  };

  const removeInstruction = (idx) => {
    setFormData(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== idx)
    }));
  };

  const addEquipment = () => {
    if (newEquipment.trim()) {
      setFormData(prev => ({
        ...prev,
        equipment: [...prev.equipment, newEquipment.trim()]
      }));
      setNewEquipment('');
    }
  };

  const removeEquipment = (idx) => {
    setFormData(prev => ({
      ...prev,
      equipment: prev.equipment.filter((_, i) => i !== idx)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="exercise-form-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {exercise ? 'Edit Exercise' : 'Add Exercise'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="exercise-name-input"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="exercise-category-select"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Muscle Groups */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Muscle Groups *</label>
              <div className="flex flex-wrap gap-2">
                {MUSCLE_GROUPS.map(mg => (
                  <button
                    key={mg.value}
                    type="button"
                    onClick={() => toggleMuscleGroup(mg.value)}
                    className={`px-3 py-1 rounded-full text-sm transition ${
                      formData.muscle_groups.includes(mg.value)
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {mg.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
              <div className="space-y-2">
                {formData.instructions.map((inst, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 w-6">{idx + 1}.</span>
                    <span className="flex-1 text-sm">{inst}</span>
                    <button
                      type="button"
                      onClick={() => removeInstruction(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newInstruction}
                    onChange={(e) => setNewInstruction(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInstruction())}
                    placeholder="Add instruction step..."
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={addInstruction}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Equipment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Equipment</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.equipment.map((eq, idx) => (
                  <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                    {eq}
                    <button type="button" onClick={() => removeEquipment(idx)} className="text-gray-500 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newEquipment}
                  onChange={(e) => setNewEquipment(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEquipment())}
                  placeholder="Add equipment..."
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <button
                  type="button"
                  onClick={addEquipment}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Add
                </button>
              </div>
            </div>

            {/* URLs */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
                <input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                data-testid="save-exercise-btn"
              >
                {saving ? 'Saving...' : (exercise ? 'Update' : 'Create')} Exercise
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ExerciseLibrary;
